import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Clock,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Layers,
  List,
} from "lucide-react";
import { format } from "date-fns";
import type { ReportRunDetail } from "@/hooks/use-admin-report-runs";

interface ReportRunDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: ReportRunDetail | null;
  loading: boolean;
}

function getStatusBadge(status: string) {
  switch (status) {
    case "completed":
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case "processing":
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
          <Activity className="h-3 w-3 mr-1 animate-pulse" />
          Processing
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function getSeverityColor(severity: string) {
  switch (severity.toLowerCase()) {
    case "error":
      return "text-red-600 bg-red-50";
    case "warning":
      return "text-yellow-600 bg-yellow-50";
    case "info":
      return "text-blue-600 bg-blue-50";
    default:
      return "text-muted-foreground bg-muted";
  }
}

export function ReportRunDetailDrawer({
  open,
  onOpenChange,
  detail,
  loading,
}: ReportRunDetailDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] p-0">
        <SheetHeader className="p-6 pb-0">
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Report Run Details
          </SheetTitle>
          {detail && (
            <SheetDescription className="font-mono text-xs">
              {detail.run.run_id}
            </SheetDescription>
          )}
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-100px)]">
          <div className="p-6 space-y-6">
            {loading ? (
              <>
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </>
            ) : !detail ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-30" />
                <p>No report selected</p>
              </div>
            ) : (
              <>
                {/* Run Overview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {getStatusBadge(detail.run.status)}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        User
                      </span>
                      <span className="text-sm font-medium">
                        {detail.run.user_email || detail.run.user_id.slice(0, 8)}...
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Exam</span>
                      <Badge variant="outline">{detail.run.exam_family_id}</Badge>
                    </div>

                    {detail.run.modality && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Modality</span>
                        <Badge>{detail.run.modality}</Badge>
                      </div>
                    )}

                    <Separator />

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Created
                      </span>
                      <span className="text-sm">
                        {format(new Date(detail.run.created_at), "PPp")}
                      </span>
                    </div>

                    {detail.run.completed_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Completed</span>
                        <span className="text-sm">
                          {format(new Date(detail.run.completed_at), "PPp")}
                        </span>
                      </div>
                    )}

                    {detail.run.duration_ms && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Duration</span>
                        <span className="text-sm font-mono">
                          {detail.run.duration_ms.toLocaleString()}ms
                        </span>
                      </div>
                    )}

                    {detail.run.token_count && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Tokens</span>
                        <span className="text-sm font-mono">
                          {detail.run.token_count.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Error Info (if failed) */}
                {detail.run.error_code && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Error Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <span className="text-xs text-muted-foreground">Code</span>
                        <p className="font-mono text-sm text-red-700">
                          {detail.run.error_code}
                        </p>
                      </div>
                      {detail.run.error_message && (
                        <div>
                          <span className="text-xs text-muted-foreground">Message</span>
                          <p className="text-sm text-red-700">
                            {detail.run.error_message}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Finding Units */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <List className="h-4 w-4" />
                      Finding Units
                      <Badge variant="secondary" className="ml-auto">
                        {detail.finding_units.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {detail.finding_units.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No finding units extracted
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {detail.finding_units.map((fu, idx) => (
                          <div
                            key={fu.finding_unit_id}
                            className="p-2 rounded bg-muted/50 text-sm"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-muted-foreground">
                                #{idx + 1}
                              </span>
                              {fu.section_token && (
                                <Badge variant="outline" className="text-xs">
                                  {fu.section_token}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm">{fu.source_text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Observation Atoms */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Observation Atoms
                      <Badge variant="secondary" className="ml-auto">
                        {detail.observation_atoms.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {detail.observation_atoms.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No observations extracted
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {detail.observation_atoms.map((atom) => (
                          <div
                            key={atom.observation_id}
                            className="p-2 rounded bg-muted/50 text-sm grid grid-cols-3 gap-2"
                          >
                            <div>
                              <span className="text-xs text-muted-foreground block">
                                Structure
                              </span>
                              <span className="font-mono text-xs">
                                {atom.structure_key}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">
                                Attribute
                              </span>
                              <span className="font-mono text-xs">
                                {atom.attribute_key}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">
                                Value
                              </span>
                              <span className="text-xs">{atom.value}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Validation Events */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Validation Events
                      <Badge variant="secondary" className="ml-auto">
                        {detail.validation_events.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {detail.validation_events.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No validation events
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {detail.validation_events.map((event) => (
                          <div
                            key={event.event_id}
                            className={`p-2 rounded text-sm ${getSeverityColor(
                              event.severity
                            )}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Badge
                                variant="outline"
                                className="text-xs uppercase"
                              >
                                {event.event_type}
                              </Badge>
                              <span className="text-xs opacity-70">
                                {format(new Date(event.created_at), "HH:mm:ss")}
                              </span>
                            </div>
                            <p className="text-sm">{event.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
